import {
  Box,
  Loader,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AulaState, User } from '@core/types/state';
import {
  HANDIN_PROVIDER,
  LTI1P3_PROVIDER,
  QUIZZES_PROVIDER,
  TURNITIN_PROVIDER,
} from '@core/assignments/constants';
import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import pushRoute from '@app/utils/routing';
import { getAssignmentsBaseRoute } from '../utils/navigation';
import { Assignment } from '@core/assignments/types';
import { createStyles } from './SubmissionsResponsive.styles';
import SubmissionSidebar from './SubmissionsSidebar/SubmissionSidebar';
import InfoAlert from './InfoAlert';
import { NO_STUDENT_EXTENSION, NO_STUDENT_MAIN_COHORT } from '../constants';
import { ExtensionUser } from './ManageExtension/ManageExtension.types';
import AssignmentHeader from '../AssignmentHeader/AssignmentHeader';
import {
  formatGroupSubmissions,
  groupSubmissionsByDate,
} from './GroupSubmissions/formatGroupSubmissions';
import AssignmentTypeProvider, {
  useAssignmentTypeProvider,
} from '../../Submissionsv4/AssignmentTypeProvider';
import { analyticsTrackEvent } from '@core/utils/analytics';
import * as analyticEvents from '@core/constants/analytics';
import SubmissionSearch, {
  SUBMISSION_SEARCH_STATUS,
  SubmissionSearchResult,
} from './SubmissionSearch';
import SubmissionAccordions, {
  ResponsiveGroupedSubmissionGroups,
  ResponsiveSubmissionGroups,
  ResponsiveSubmissionItem,
} from './SubmissionAccordions';

const resolveAssignmentId = (assignment?: { id?: string; objectId?: string }) =>
  assignment?.id || assignment?.objectId || '';

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * Fallback: derive the submission segment from Redux routing state.
 * Used only when the router hasn't injected submissionId via params
 * (e.g. on the base `.../submissions` route).
 */
const selectCurrentSubmissionId = (state: AulaState): string => {
  const { routing } = state;
  if (!routing) return '';
  const { pathname } = routing.locationBeforeTransitions;
  const idx = pathname.search('/submissions');
  if (idx === -1) return '';
  const [segment] = pathname
    .substring(idx)
    .replace('/submissions', '')
    .replace(/^\//, '')
    .split('/');
  return segment || '';
};

const groupSearchResults = (
  results: SubmissionSearchResult<ResponsiveSubmissionItem>[]
): ResponsiveSubmissionGroups => ({
  earlySubmissions: results
    .filter(({ status }) => status === SUBMISSION_SEARCH_STATUS.onTime)
    .map(({ submission }) => submission),
  lateSubmissions: results
    .filter(({ status }) => status === SUBMISSION_SEARCH_STATUS.late)
    .map(({ submission }) => submission),
  noSubmissions: results
    .filter(({ status }) => status === SUBMISSION_SEARCH_STATUS.noSubmission)
    .map(({ submission }) => submission),
});

const filterGroupedSubmissionGroups = (
  groups: ResponsiveGroupedSubmissionGroups,
  results: SubmissionSearchResult<ResponsiveSubmissionItem>[]
): ResponsiveGroupedSubmissionGroups => {
  const visibleStudentIds = new Set(
    results.map(({ submission }) => String(submission.user.id))
  );
  const filterGroups = (
    groupedSubmissions: ResponsiveGroupedSubmissionGroups['earlySubmissions']
  ) =>
    groupedSubmissions
      .map((group) => ({
        ...group,
        submissions: group.submissions.filter(({ user }) =>
          visibleStudentIds.has(String(user.id))
        ),
      }))
      .filter(({ submissions }) => submissions.length > 0);

  return {
    earlySubmissions: filterGroups(groups.earlySubmissions),
    lateSubmissions: filterGroups(groups.lateSubmissions),
    noSubmissions: filterGroups(groups.noSubmissions),
  };
};

// ---------------------------------------------------------------------------
// Empty-cohort alerts
// ---------------------------------------------------------------------------
type EmptyCohortAlertProps = { isExtension: boolean };

const EmptyCohortAlert = ({ isExtension }: EmptyCohortAlertProps) => {
  const { TITLE, MESSAGE } = isExtension
    ? NO_STUDENT_EXTENSION
    : NO_STUDENT_MAIN_COHORT;

  return (
    <InfoAlert title={TITLE} sx={{ mt: '1.5rem' }}>
      {MESSAGE}
    </InfoAlert>
  );
};

type SubmissionContentProps = {
  accessingAsStaff: boolean;
  assignment: SubmissionAssignment;
  filterControl?: React.ReactNode;
  groupedSubmissionGroups?: ResponsiveGroupedSubmissionGroups;
  isEducator: boolean;
  loading: boolean;
  onDeleteSubmission: (submission: ResponsiveSubmissionItem) => Promise<void>;
  parentAssignment: SubmissionAssignment;
  showEmptyCohortAlert: boolean;
  spaceId: string;
  submissionGroups: ResponsiveSubmissionGroups;
  totalStudentCount: number;
  totalSubmissionCount: number;
};

const SubmissionContent = ({
  accessingAsStaff,
  assignment,
  filterControl,
  groupedSubmissionGroups,
  isEducator,
  loading,
  onDeleteSubmission,
  parentAssignment,
  showEmptyCohortAlert,
  spaceId,
  submissionGroups,
  totalStudentCount,
  totalSubmissionCount,
}: SubmissionContentProps) => {
  const { launchFeedback, launchSubmission, type } =
    useAssignmentTypeProvider();

  const summaryAssignment = {
    ...assignment,
    description: parentAssignment.description,
    linkedMaterials: parentAssignment.linkedMaterials,
    title: parentAssignment.title,
  } as Assignment;
  const submissionCohortName = assignment.parentAssignmentId
    ? assignment.title
    : 'Main cohort';
  const openSubmission = (submission: ResponsiveSubmissionItem) => {
    analyticsTrackEvent(analyticEvents.VIEW_STUDENT_SUBMISSION, {
      assignmentId: assignment.id,
    });

    if (type === QUIZZES_PROVIDER) {
      launchFeedback({ submissionId: submission.id });
      return;
    }

    if (type === HANDIN_PROVIDER) {
      launchSubmission({
        submissionId: submission.externalId,
        studentId: String(submission.user.id),
      });
      return;
    }

    launchSubmission({
      submissionExternalId: submission.externalId,
      submissionId: submission.id,
      studentId: String(submission.user.id),
    });
  };

  const renderAccordions = (
    groups: ResponsiveSubmissionGroups,
    groupedGroups = groupedSubmissionGroups
  ) => (
    <SubmissionAccordions
      accessingAsStaff={accessingAsStaff}
      groupedSubmissionGroups={groupedGroups}
      hideNoSubmissionIdentities={Boolean(assignment.isAnonymised)}
      isEducator={isEducator}
      isGroupAssignment={Boolean(assignment.groupSet)}
      onDeleteSubmission={onDeleteSubmission}
      onOpenSubmission={openSubmission}
      provider={assignment.provider || HANDIN_PROVIDER}
      submissionGroups={groups}
    />
  );

  const searchableGroups = assignment.isAnonymised
    ? { ...submissionGroups, noSubmissions: [] }
    : submissionGroups;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Loader size={32} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
        maxWidth: '792px',
        mx: 'auto',
        minWidth: 0,
      }}
    >
      <AssignmentHeader
        assignment={summaryAssignment}
        page="overview"
        isEducator={isEducator}
        accessingAsStaff={accessingAsStaff}
        currentSpaceId={spaceId}
        totalStudentCount={totalStudentCount}
        totalSubmissionCount={totalSubmissionCount}
        submissionCohortName={submissionCohortName}
      />
      {showEmptyCohortAlert ? (
        <EmptyCohortAlert
          isExtension={Boolean(assignment.parentAssignmentId)}
        />
      ) : (
        <SubmissionSearch
          filterControl={filterControl}
          isVisible={isEducator && !accessingAsStaff}
          renderResults={(results) =>
            renderAccordions(
              groupSearchResults(results),
              groupedSubmissionGroups
                ? filterGroupedSubmissionGroups(
                    groupedSubmissionGroups,
                    results
                  )
                : undefined
            )
          }
          submissionGroups={searchableGroups}
        >
          {renderAccordions(submissionGroups)}
        </SubmissionSearch>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export type SubmissionAssignment = {
  classRoom?: { objectId: string };
  description?: string;
  endDate?: string;
  externalId?: string;
  gradesReleasedAt?: string;
  gradeType?: string;
  groupSet?: string;
  id: string;
  isHidden?: boolean;
  isAnonymised?: boolean;
  linkedMaterials?: Array<{ id?: string; title?: string }>;
  objectId?: string;
  parentAssignmentId?: string;
  provider?: string;
  providerId?: string;
  resourceLinkId?: string;
  shouldCloseOnDueDate?: boolean;
  startDate?: string;
  status?: string;
  students?: string[];
  studentsCount?: number;
  submissionsCount?: number;
  submissions?: string[];
  title: string;
  extensions?: any[];
};

type Props = {
  accessToken?: string;
  accessingAsStaff: boolean;
  assignment?: SubmissionAssignment;
  assignmentCreationError?: any;
  assignmentUpdateError?: any;
  assignmentsErrored: boolean;
  createExtension: (params: any) => Promise<SubmissionAssignment | null>;
  deleteSubmission: (params: {
    assignmentExternalId: string;
    assignmentId: string;
    groupId?: string;
    spaceId: string;
    studentId?: string | number;
  }) => Promise<any>;
  editExtension: (params: any) => Promise<SubmissionAssignment | null>;
  extensions?: SubmissionAssignment[];
  fetchAssignments: (params: {
    accessToken: string;
    spaceId: string;
    user: User;
  }) => Promise<void>;
  fetchSubmissions: (params: {
    accessToken: string;
    assignmentId: string;
    group?: string | null;
  }) => Promise<any> | void;
  isFetchingAssignments: boolean;
  isEducator: boolean;
  loadStudents: (spaceId: string) => Promise<any[]>;
  params?: {
    assignmentId?: string;
    submissionId?: string;
  };
  spaceId: string;
  resetExtensionErrors: () => void;
  user: (User & ExtensionUser) | null;
  fetchAllSubmissionsIncludingExtensions: (params: {
    accessToken: string;
    assignmentsIds: string[];
  }) => Promise<any> | void;
  hasFetchedAssignments: boolean;
  initialExtensionId: string | null;
  getSubmissions: (assignmentId: string) => any[];
  getSubmissionsErrorStatus: (assignmentId: string) => boolean;
  getSubmissionsFetchedStatus: (assignmentId: string) => boolean;
  getSubmissionsFetchingStatus: (assignmentId: string) => boolean;
  parentAssignmentSubmissions: any[];
  hasExtensions: boolean;
  sets: Array<{
    externalId?: string;
    isHeader?: boolean;
    key: string;
    label: string;
  }>;
};

const SubmissionsResponsive = ({
  accessToken,
  accessingAsStaff,
  assignment,
  assignmentCreationError,
  assignmentUpdateError,
  assignmentsErrored,
  createExtension,
  deleteSubmission,
  editExtension,
  extensions = [] as SubmissionAssignment[],
  fetchAssignments,
  fetchSubmissions,
  fetchAllSubmissionsIncludingExtensions,
  hasFetchedAssignments,
  isFetchingAssignments,
  isEducator,
  initialExtensionId,
  loadStudents,
  params,
  spaceId,
  resetExtensionErrors,
  user,
  getSubmissions,
  getSubmissionsErrorStatus,
  getSubmissionsFetchedStatus,
  getSubmissionsFetchingStatus,
  parentAssignmentSubmissions,
  hasExtensions,
  sets,
}: Props) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const {
    webAppFtSpaceArea202504,
    webappFtHandinAnonymousExtensions202110,
    webappFtTurnitinAnonymousExtensions202110,
  } = useFlags();
  const staffAlertOffset = webAppFtSpaceArea202504 && accessingAsStaff ? 56 : 0;
  const styles = createStyles(theme, staffAlertOffset);
  const [assignmentLoading, setAssignmentsLoading] = useState(false);
  const [submissionCohortsLoading, setSubmissionCohortsLoading] =
    useState(false);
  const assignmentId = params?.assignmentId;

  const assignmentKey = resolveAssignmentId(assignment);

  const refreshAssignments = async (savedExtension?: SubmissionAssignment) => {
    if (!accessToken || !spaceId || !user) return;
    await fetchAssignments({ accessToken, spaceId, user });
    if (savedExtension?.id) {
      // Match Submissionsv4's openExtension: make the saved extension active
      // and ensure its submissions are available immediately.
      const extensionId =
        savedExtension.provider === TURNITIN_PROVIDER
          ? savedExtension.objectId || savedExtension.id
          : savedExtension.id;
      setSelectedExtensionId(extensionId);
      fetchSubmissions({
        accessToken,
        assignmentId: extensionId,
      });
      pushRoute(
        `${getAssignmentsBaseRoute(
          spaceId
        )}/${assignmentId}/submissions/${extensionId}`
      );
    }
  };

  const loadAssignments = async (
    accessToken: string,
    spaceId: string,
    user: User
  ) => {
    setAssignmentsLoading(true);
    try {
      await fetchAssignments({ accessToken, spaceId, user });
    } finally {
      setAssignmentsLoading(false);
    }
  };

  useEffect(() => {
    if (
      hasFetchedAssignments ||
      isFetchingAssignments ||
      !accessToken ||
      !spaceId ||
      !user
    ) {
      return;
    }
    loadAssignments(accessToken, spaceId, user);
  }, [
    accessToken,
    hasFetchedAssignments,
    fetchAssignments,
    isFetchingAssignments,
    spaceId,
    user,
  ]);

  // Prefer the submissionId injected by the router (available immediately on
  // hard refresh) and fall back to parsing the Redux routing state (used when
  // landing on the base `.../submissions` route that has no :submissionId param).
  const reduxSubmissionId = useSelector(selectCurrentSubmissionId);
  const currentSubmissionId = params?.submissionId ?? reduxSubmissionId;

  // Treat "no trailing segment" the same as "list" — this is the submissions
  // list view. A refresh on `.../submissions` should keep the list visible, not
  // redirect anywhere.
  const isListView = !currentSubmissionId || currentSubmissionId === 'list';

  const extensionIdFromRoute =
    currentSubmissionId && currentSubmissionId !== 'list'
      ? currentSubmissionId
      : '';

  // Get the extension id from the route or the initial extension id or the assignment id
  const currentExtensionId = useMemo(() => {
    if (extensionIdFromRoute) return extensionIdFromRoute;
    if (initialExtensionId) return initialExtensionId;
    if (isMdUp && assignmentKey) return assignmentKey;
    return '';
  }, [extensionIdFromRoute, initialExtensionId, isMdUp, assignmentKey]);

  const [selectedExtensionId, setSelectedExtensionId] = useState(
    currentExtensionId || assignmentKey || ''
  );
  const [selectedGroupId, setSelectedGroupId] = useState('');

  useEffect(() => {
    if (currentExtensionId) {
      setSelectedExtensionId(currentExtensionId);
      setSelectedGroupId('');
    }
  }, [currentExtensionId]);

  const anonymousExtensionsEnabled = (() => {
    if (!assignment?.isAnonymised) return true;
    const provider =
      assignment.provider === 'handin' ? HANDIN_PROVIDER : assignment.provider;
    if (provider === HANDIN_PROVIDER) {
      return Boolean(webappFtHandinAnonymousExtensions202110);
    }
    if (provider === TURNITIN_PROVIDER) {
      return Boolean(webappFtTurnitinAnonymousExtensions202110);
    }
    return false;
  })();
  const canCreateExtensions = Boolean(
    assignment &&
      isEducator &&
      !accessingAsStaff &&
      !assignment.groupSet &&
      assignment.provider !== QUIZZES_PROVIDER &&
      assignment.provider !== LTI1P3_PROVIDER &&
      anonymousExtensionsEnabled
  );
  const canEditExtensions = isEducator;

  useEffect(() => {
    if ((!isEducator && !accessingAsStaff) || !accessToken || !assignmentId)
      return;

    let active = true;
    const fetchSubmissionData = async () => {
      if (hasExtensions && assignment) {
        const assignmentsIds = [
          assignmentId,
          ...(assignment.extensions || []).map(
            (extension) => extension.id || extension.objectId
          ),
        ].filter(Boolean) as string[];

        setSubmissionCohortsLoading(true);
        try {
          await fetchAllSubmissionsIncludingExtensions({
            accessToken,
            assignmentsIds,
          });
        } finally {
          if (active) setSubmissionCohortsLoading(false);
        }
        return;
      }

      fetchSubmissions({ accessToken, assignmentId });
    };

    fetchSubmissionData();
    return () => {
      active = false;
    };
  }, [
    accessToken,
    assignment,
    assignmentId,
    fetchAllSubmissionsIncludingExtensions,
    fetchSubmissions,
    hasExtensions,
    isEducator,
    accessingAsStaff,
  ]);

  const assignmentRoute = params?.assignmentId
    ? `${getAssignmentsBaseRoute(spaceId)}/${params.assignmentId}`
    : `${getAssignmentsBaseRoute(spaceId)}/list`;

  const submissionsBaseRoute = `${getAssignmentsBaseRoute(spaceId)}/${
    params?.assignmentId
  }/submissions`;

  // Desktop always shows sidebar + detail
  useEffect(() => {
    if (!isMdUp || !assignmentKey || !params?.assignmentId || !isListView) {
      return;
    }

    const targetExtensionId = initialExtensionId || assignmentKey;
    pushRoute(`${submissionsBaseRoute}/${targetExtensionId}`);
  }, [
    assignmentKey,
    initialExtensionId,
    isListView,
    isMdUp,
    params?.assignmentId,
    submissionsBaseRoute,
  ]);

  // Mobile deep-link from home (`?extensionId=`) opens the extension detail view.
  useEffect(() => {
    if (
      isMdUp ||
      !initialExtensionId ||
      !isListView ||
      !assignmentKey ||
      !extensions.length
    ) {
      return;
    }

    const extensionExists = extensions.some(
      (extension) =>
        String(extension.id) === String(initialExtensionId) ||
        String(extension.objectId) === String(initialExtensionId)
    );

    if (!extensionExists) return;

    pushRoute(`${submissionsBaseRoute}/${initialExtensionId}`);
  }, [
    assignmentKey,
    extensions,
    initialExtensionId,
    isListView,
    isMdUp,
    submissionsBaseRoute,
  ]);

  const selectExtension = (extension: SubmissionAssignment) => {
    const extensionId =
      extension.provider === TURNITIN_PROVIDER
        ? extension.objectId || extension.id
        : extension.id;

    setSelectedExtensionId(extensionId);
    setSelectedGroupId('');
    if (accessToken) {
      fetchSubmissions({ accessToken, assignmentId: extensionId });
    }
    pushRoute(`${submissionsBaseRoute}/${extensionId}`);
  };

  const selectedAssignmentSource = hasExtensions
    ? extensions.find(
        (extension) =>
          extension.id === selectedExtensionId ||
          extension.objectId === selectedExtensionId
      )
    : assignment;
  const currentAssignment = useMemo(
    () =>
      selectedAssignmentSource?.provider === 'handin'
        ? { ...selectedAssignmentSource, provider: HANDIN_PROVIDER }
        : selectedAssignmentSource,
    [selectedAssignmentSource]
  );
  const submissionKeys = currentAssignment
    ? Array.from(
        new Set(
          [
            selectedExtensionId,
            currentAssignment.id,
            currentAssignment.objectId,
          ].filter(Boolean) as string[]
        )
      )
    : [];
  const selectedSubmissionKey =
    submissionKeys.find(
      (key) =>
        getSubmissions(key).length > 0 ||
        getSubmissionsFetchedStatus(key) ||
        getSubmissionsFetchingStatus(key) ||
        getSubmissionsErrorStatus(key)
    ) ||
    submissionKeys[0] ||
    '';
  const currentSubmissions = selectedSubmissionKey
    ? (getSubmissions(selectedSubmissionKey) as ResponsiveSubmissionItem[])
    : (parentAssignmentSubmissions as ResponsiveSubmissionItem[]);
  const isGroupAssignment = Boolean(assignment?.groupSet);
  const submissionsForDisplay = isGroupAssignment
    ? (parentAssignmentSubmissions as ResponsiveSubmissionItem[])
    : currentSubmissions;
  const submissionGroups = useMemo(
    () =>
      groupSubmissionsByDate(
        submissionsForDisplay,
        currentAssignment?.endDate || assignment?.endDate || ''
      ) as ResponsiveSubmissionGroups,
    [assignment?.endDate, currentAssignment?.endDate, submissionsForDisplay]
  );
  const groupedSubmissionGroups = useMemo(
    () =>
      isGroupAssignment
        ? (groupSubmissionsByDate(
            formatGroupSubmissions(submissionsForDisplay),
            currentAssignment?.endDate || assignment?.endDate || ''
          ) as ResponsiveGroupedSubmissionGroups)
        : undefined,
    [
      assignment?.endDate,
      currentAssignment?.endDate,
      isGroupAssignment,
      submissionsForDisplay,
    ]
  );
  const countGroups = groupedSubmissionGroups || submissionGroups;
  const totalStudentCount =
    countGroups.earlySubmissions.length +
    countGroups.lateSubmissions.length +
    countGroups.noSubmissions.length;
  const totalSubmissionCount =
    countGroups.earlySubmissions.length + countGroups.lateSubmissions.length;
  const allSubmissions = Object.values(submissionGroups).flat();
  const showEmptyCohortAlert =
    !isGroupAssignment && hasExtensions && allSubmissions.length === 0;
  const submissionsLoading = Boolean(
    assignmentLoading ||
      submissionCohortsLoading ||
      isFetchingAssignments ||
      (selectedSubmissionKey &&
        getSubmissionsFetchingStatus(selectedSubmissionKey)) ||
      (!hasExtensions &&
        selectedSubmissionKey &&
        !getSubmissionsFetchedStatus(selectedSubmissionKey) &&
        !getSubmissionsErrorStatus(selectedSubmissionKey))
  );
  const submissionsErrored = Boolean(
    selectedSubmissionKey && getSubmissionsErrorStatus(selectedSubmissionKey)
  );
  const groupFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    return (sets || [])
      .filter(
        ({ isHeader, key }) =>
          !isHeader && key !== 'manage-groups' && key !== 'footer-actions'
      )
      .filter(({ key }) => {
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(({ key, label }) => ({ label, value: key }));
  }, [sets]);

  const handleGroupFilterChange = (groupId: string) => {
    if (!accessToken || !selectedSubmissionKey) return;

    setSelectedGroupId(groupId);
    fetchSubmissions({
      accessToken,
      assignmentId: selectedSubmissionKey,
      group: groupId || null,
    });
    if (groupId) {
      analyticsTrackEvent(analyticEvents.FILTER_SUBMISSIONS_BY_GROUP_ID, {
        activeClassroom: spaceId,
        assignmentId: currentAssignment?.id,
        groupId,
      });
    }
  };

  const filterControl = (
    <Select
      inputProps={{ 'aria-label': 'Filter submissions by group' }}
      label="Filter by group"
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        handleGroupFilterChange(String(event.target.value))
      }
      options={[{ label: 'All groups', value: '' }, ...groupFilterOptions]}
      sx={{
        mt: 0,
        '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
      }}
      value={selectedGroupId}
    />
  );

  const handleDeleteSubmission = async (
    submissionToDelete: ResponsiveSubmissionItem
  ) => {
    if (!currentAssignment?.externalId) {
      throw new Error(
        'The submission cannot be deleted without an assignment ID.'
      );
    }

    const result = await deleteSubmission({
      assignmentExternalId: currentAssignment.externalId,
      assignmentId: currentAssignment.id,
      spaceId,
      ...(isGroupAssignment && submissionToDelete.group?.id
        ? { groupId: submissionToDelete.group.id }
        : { studentId: submissionToDelete.user.id }),
    });

    if (result?.status === 'error') {
      throw new Error('The submission could not be deleted.');
    }
  };

  const submissionContent =
    assignmentsErrored || submissionsErrored ? (
      <Typography color="error" role="alert" sx={{ p: 2 }}>
        An error occurred while loading submissions. Please refresh.
      </Typography>
    ) : currentAssignment && assignment ? (
      <AssignmentTypeProvider
        assignment={currentAssignment as any}
        spaceId={spaceId}
        user={user || undefined}
      >
        <SubmissionContent
          accessingAsStaff={accessingAsStaff}
          assignment={currentAssignment}
          filterControl={filterControl}
          groupedSubmissionGroups={groupedSubmissionGroups}
          isEducator={isEducator}
          key={selectedSubmissionKey}
          loading={submissionsLoading}
          onDeleteSubmission={handleDeleteSubmission}
          parentAssignment={assignment}
          showEmptyCohortAlert={showEmptyCohortAlert}
          spaceId={spaceId}
          submissionGroups={submissionGroups}
          totalStudentCount={totalStudentCount}
          totalSubmissionCount={totalSubmissionCount}
        />
      </AssignmentTypeProvider>
    ) : assignmentLoading || isFetchingAssignments ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Loader size={32} />
      </Box>
    ) : (
      <Typography color="text.secondary" role="status" sx={{ p: 2 }}>
        This assignment is unavailable. Return to assignments and try again.
      </Typography>
    );

  if (!isEducator && !accessingAsStaff) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography color="text.secondary">
          Submission management is available to educators and staff.
        </Typography>
      </Box>
    );
  }

  // ── Desktop: sidebar + content always side-by-side ─────────────────────────
  if (isMdUp) {
    return (
      <Box sx={styles.desktopShell}>
        <Box sx={styles.leftWrapper}>
          <SubmissionSidebar
            accessToken={accessToken}
            assignment={assignment}
            assignmentCreationError={assignmentCreationError}
            assignmentUpdateError={assignmentUpdateError}
            backRoute={assignmentRoute}
            canCreateExtensions={canCreateExtensions}
            canEditExtensions={canEditExtensions}
            createExtension={createExtension}
            editExtension={editExtension}
            extensions={extensions}
            loadStudents={loadStudents}
            onExtensionSaved={refreshAssignments}
            onSelectExtension={selectExtension}
            resetExtensionErrors={resetExtensionErrors}
            selectedExtensionId={selectedExtensionId}
            spaceId={spaceId}
            user={user}
          />
        </Box>
        <Box sx={styles.contentArea}>{submissionContent}</Box>
      </Box>
    );
  }

  // ── Mobile list view ────────────────────────────────────────────────────────
  if (isListView) {
    return (
      <Box sx={{ height: '100%' }}>
        <SubmissionSidebar
          accessToken={accessToken}
          assignment={assignment}
          assignmentCreationError={assignmentCreationError}
          assignmentUpdateError={assignmentUpdateError}
          backRoute={assignmentRoute}
          canCreateExtensions={canCreateExtensions}
          canEditExtensions={canEditExtensions}
          createExtension={createExtension}
          editExtension={editExtension}
          extensions={extensions}
          loadStudents={loadStudents}
          onExtensionSaved={refreshAssignments}
          onSelectExtension={selectExtension}
          resetExtensionErrors={resetExtensionErrors}
          selectedExtensionId={selectedExtensionId}
          spaceId={spaceId}
          user={user}
        />
      </Box>
    );
  }

  // ── Mobile detail view ──────────────────────────────────────────────────────
  // Back button goes to the submissions list (`.../submissions/list`).
  // White container wraps the detail content, matching the Assignment detail
  // mobile layout (AssignmentResponsive → pageContent).
  const submissionsListRoute = `${submissionsBaseRoute}/list`;

  return (
    <Box sx={styles.pageWrapper}>
      <Box sx={styles.buttonWrapper}>
        <NavigationBackHeader route={submissionsListRoute} />
      </Box>
      <Box sx={styles.pageContent}>
        <Box sx={styles.assignmentDetailsMobile}>{submissionContent}</Box>
      </Box>
    </Box>
  );
};

export default SubmissionsResponsive;
